import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { OptionWithCheckbox } from '../atoms/OptionWithCheckbox.atom';
import { MultiSelectFieldProps } from '../SelectField.types';
import { SimpleMultiValue } from '../atoms/SimpleMultiValue.atom';
import { CSelectFieldBase } from '../SelectField.component';
import { SearchInput } from '../atoms/SearchInput.atom';
import { DnDControl } from '../atoms/DnDControl.atom';
import { MultiSelectMenu } from '../atoms/MultiSelectMenu.atom';
import { IconSearchInput } from '../atoms/IconSearchInput.atom';
import { OptionType } from '../../../Types';
import { CSelectFieldPaginated } from './SelectFieldPaginated';
import { IconControl } from '../atoms/IconControl.atom';

export const CMultiSelectField = <T extends string[] | number[] = string[]>({
    ...props
}: MultiSelectFieldProps<T>): JSX.Element => {
    const components = {
        Option: OptionWithCheckbox,
    };
    return <CSelectFieldBase<T> {...props} isMulti components={components} />;
};

export const CSortableMultiSelectField = <
    T extends string[] | number[] = string[]
>({
    ...props
}: MultiSelectFieldProps<T>): JSX.Element => {
    const components = {
        Option: OptionWithCheckbox,
        Control: DnDControl as any,
        MultiValue: SimpleMultiValue,
        Menu: MultiSelectMenu,
        Input: SearchInput,
    };
    return (
        <CSelectFieldBase<T>
            {...props}
            isMulti
            isClearable={false}
            resetSearchOnSelection
            placeholder=""
            components={components}
            hideSelectAll
            backspaceRemovesValue={false}
        />
    );
};

export const CSplitSelectField = <T extends string[] | number[] = string[]>({
    isMulti = true,
    isPaginated = false,
    fetchOptions,
    height = 32,
    icon,
    ...props
}: MultiSelectFieldProps<T> & {
    isMulti?: boolean;
    isPaginated?: boolean;
    fetchOptions?: (
        searchValue: string | undefined,
        page: number
    ) => Promise<OptionType<string | number>[]>;
    trackedPagination?: {
        page: number;
        count: number;
    };
    defaultValues?: OptionType[];
    icon?: IconDefinition;
}): JSX.Element => {
    const components = {
        Option: OptionWithCheckbox,
        MultiValue: SimpleMultiValue,
        Menu: MultiSelectMenu,
        Input: IconSearchInput,
        ...(icon && { Control: IconControl }),
        LoadingIndicator: () => null, // this will overflow, we are handling loading in IconSearchInput
    };
    if (isPaginated && fetchOptions)
        return (
            <CSelectFieldPaginated<T>
                {...props}
                icon={icon}
                fetchOptions={fetchOptions}
                isMulti={isMulti}
                shouldWrap={false}
                isClearable={false}
                placeholder=""
                components={components}
                height={height}
            />
        );

    return (
        <CSelectFieldBase<T>
            {...props}
            icon={icon}
            isMulti={isMulti}
            shouldWrap={false}
            isClearable={false}
            resetSearchOnSelection
            placeholder=""
            components={components}
            height={height}
        />
    );
};

export const CSimpleMultiSelectField = <
    T extends string[] | number[] = string[]
>({
    testId,
    ...props
}: MultiSelectFieldProps<T>): JSX.Element => {
    const components = {
        Option: OptionWithCheckbox,
        MultiValue: SimpleMultiValue,
        Menu: MultiSelectMenu,
        Input: SearchInput,
    };
    return (
        <CSelectFieldBase<T>
            {...props}
            testId={testId}
            isMulti
            isClearable={false}
            components={components}
            backspaceRemovesValue={false}
            placeholder=""
        />
    );
};

export const CSimpleMultiSelectFieldWithIcon = <
    T extends string[] | number[] = string[]
>({
    ...props
}: MultiSelectFieldProps<T> & {
    icon: IconDefinition;
}): JSX.Element => {
    const components = {
        Option: OptionWithCheckbox,
        Menu: MultiSelectMenu,
        Input: SearchInput,
        MultiValue: SimpleMultiValue,
        Control: IconControl,
    };
    return (
        <CSelectFieldBase<T>
            {...props}
            isMulti
            hideCount
            isClearable={false}
            components={components}
            backspaceRemovesValue={false}
            placeholder=""
        />
    );
};

export const CMultiSelectFieldWithIcon = <
    T extends string[] | number[] = string[]
>({
    ...props
}: MultiSelectFieldProps<T> & {
    icon: IconDefinition;
}): JSX.Element => {
    const components = {
        Option: OptionWithCheckbox,
        Control: IconControl,
    };
    return <CSelectFieldBase<T> {...props} isMulti components={components} />;
};
